import { useEffect, useState } from "react";
import { useRouter } from "../lib/router";
import { useAuth } from "../lib/auth-context";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Card, CardHeader, CardTitle, CardContent } from "../components/ui/card";
import type { WordReplacementMap } from "../lib/word-replacement";
import { loadReplacements, addReplacement, deleteReplacement, resetToDefaults } from "../lib/replacement-store";
import {
  loadSurveyTemplates,
  saveSurveyTemplates,
  type SurveyTemplate,
} from "../lib/survey-templates";

export default function SettingsPage() {
  const { navigate } = useRouter();
  const { auth } = useAuth();
  const [replacements, setReplacements] = useState<WordReplacementMap>({});
  const [newPattern, setNewPattern] = useState("");
  const [newReplacement, setNewReplacement] = useState("");
  const [filter, setFilter] = useState("");

  // Survey templates state
  const [templates, setTemplates] = useState<SurveyTemplate[]>([]);
  const [questionsText, setQuestionsText] = useState<Record<string, string>>({});
  const [savingTemplates, setSavingTemplates] = useState(false);
  const [templatesStatus, setTemplatesStatus] = useState<{ kind: "ok" | "err"; msg: string } | null>(null);

  useEffect(() => {
    loadReplacements().then(({ map }) => setReplacements(map));
  }, []);

  useEffect(() => {
    loadSurveyTemplates().then((t) => {
      setTemplates(t);
      // Convert each template's questions[] into a textarea-friendly newline string
      const text: Record<string, string> = {};
      for (const tpl of t) text[tpl.id] = tpl.questions.join("\n");
      setQuestionsText(text);
    });
  }, []);

  const handleAdd = () => {
    if (!newPattern.trim() || !newReplacement.trim()) return;
    const updated = addReplacement(newPattern, newReplacement);
    setReplacements(updated);
    setNewPattern("");
    setNewReplacement("");
  };

  const handleDelete = (pattern: string) => {
    const updated = deleteReplacement(pattern);
    setReplacements(updated);
  };

  const handleReset = async () => {
    const updated = await resetToDefaults();
    setReplacements(updated);
  };

  const entries = Object.entries(replacements).filter(
    ([k, v]) =>
      !filter ||
      k.toLowerCase().includes(filter.toLowerCase()) ||
      v.toLowerCase().includes(filter.toLowerCase())
  );

  // Survey template handlers
  const updateTemplateName = (idx: number, name: string) => {
    setTemplates(prev => prev.map((t, i) => (i === idx ? { ...t, name } : t)));
  };

  const updateTemplateQuestions = (id: string, raw: string) => {
    setQuestionsText(prev => ({ ...prev, [id]: raw }));
  };

  const deleteTemplate = (idx: number) => {
    const t = templates[idx];
    if (!confirm(`Delete survey "${t?.name}"? This cannot be undone after saving.`)) return;
    setTemplates(prev => prev.filter((_, i) => i !== idx));
  };

  const addTemplate = () => {
    const id = prompt("New survey id (lowercase, hyphens only — e.g. fiber-uplinks):")?.trim();
    if (!id) return;
    if (!/^[a-z0-9-]+$/.test(id)) {
      alert("Id must be lowercase letters, numbers, and hyphens only.");
      return;
    }
    if (templates.some(t => t.id === id)) {
      alert("That id is already in use.");
      return;
    }
    const newTpl: SurveyTemplate = { id, name: id, questions: [] };
    setTemplates(prev => [...prev, newTpl]);
    setQuestionsText(prev => ({ ...prev, [id]: "" }));
  };

  const handleSaveTemplates = async () => {
    if (!auth) {
      setTemplatesStatus({ kind: "err", msg: "Not signed in" });
      return;
    }
    // Materialize questions from textarea state back into the templates array
    const toSave: SurveyTemplate[] = templates.map(t => ({
      ...t,
      questions: (questionsText[t.id] ?? "").split("\n").map(q => q.trim()).filter(Boolean),
    }));

    // Light client-side validation
    const namelessIdx = toSave.findIndex(t => !t.name.trim());
    if (namelessIdx >= 0) {
      setTemplatesStatus({ kind: "err", msg: `Template "${toSave[namelessIdx].id}" has an empty name` });
      return;
    }

    setSavingTemplates(true);
    setTemplatesStatus(null);
    const result = await saveSurveyTemplates(toSave, auth.accessToken);
    setSavingTemplates(false);

    if (result.ok) {
      setTemplates(toSave);
      setTemplatesStatus({ kind: "ok", msg: `Saved ${toSave.length} templates` });
      setTimeout(() => setTemplatesStatus(null), 3000);
    } else {
      setTemplatesStatus({ kind: "err", msg: result.error ?? "Save failed" });
    }
  };

  return (
    <div className="min-h-screen p-6 max-w-2xl mx-auto">
      <header className="flex items-center justify-between mb-6">
        <Button variant="ghost" onClick={() => navigate("/")}>
          &larr; Back
        </Button>
        <h1 className="text-xl font-bold">Settings</h1>
        <div />
      </header>

      <Card className="mb-6">
        <CardHeader>
          <CardTitle>Word Replacement Library</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <p className="text-sm text-muted-foreground">
            These replacements are applied to transcribed text to fix common
            speech-to-text errors with technical terms.{" "}
            <strong>{Object.keys(replacements).length}</strong> terms loaded.
          </p>

          <div className="flex gap-2">
            <Input
              value={newPattern}
              onChange={(e) => setNewPattern(e.target.value)}
              placeholder="Spoken pattern (e.g., 'v lan')"
              className="flex-1"
            />
            <Input
              value={newReplacement}
              onChange={(e) => setNewReplacement(e.target.value)}
              placeholder="Replacement (e.g., 'VLAN')"
              className="flex-1"
            />
            <Button onClick={handleAdd}>Add</Button>
          </div>

          <div className="flex items-center gap-2">
            <Input
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
              placeholder="Filter..."
              className="flex-1"
            />
            <Button variant="outline" onClick={handleReset}>
              Reset to Defaults
            </Button>
          </div>

          <div className="max-h-96 overflow-y-auto space-y-1">
            {entries.map(([pattern, replacement]) => (
              <div
                key={pattern}
                className="flex items-center justify-between py-1.5 px-2 rounded hover:bg-muted/50 text-sm"
              >
                <span className="text-muted-foreground">{pattern}</span>
                <span className="font-medium">&rarr; {replacement}</span>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => handleDelete(pattern)}
                  className="text-destructive h-6 px-2"
                >
                  &times;
                </Button>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      <Card className="mb-6">
        <CardHeader>
          <CardTitle>Survey Templates</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <p className="text-sm text-muted-foreground">
            These templates appear in the New Survey screen. Edit the questions for an existing
            survey, or add a new one. <strong>{templates.length}</strong> templates loaded.
            Changes are saved server-side and apply to all VoxDoc users.
          </p>

          <div className="flex items-center gap-2">
            <Button onClick={addTemplate} variant="outline">+ Add Template</Button>
            <Button onClick={handleSaveTemplates} disabled={savingTemplates}>
              {savingTemplates ? "Saving..." : "Save All Templates"}
            </Button>
            {templatesStatus && (
              <span
                className={`text-sm ${
                  templatesStatus.kind === "ok" ? "text-emerald-500" : "text-destructive"
                }`}
              >
                {templatesStatus.msg}
              </span>
            )}
          </div>

          <div className="space-y-3">
            {templates.map((t, idx) => (
              <details key={t.id} className="border rounded-lg p-3 bg-muted/30">
                <summary className="cursor-pointer flex items-center justify-between">
                  <span>
                    <span className="font-semibold">{t.name}</span>
                    <span className="text-xs text-muted-foreground ml-2">
                      ({(questionsText[t.id]?.split("\n").filter(s => s.trim()).length ?? 0)} questions)
                    </span>
                  </span>
                  <code className="text-xs text-muted-foreground">{t.id}</code>
                </summary>
                <div className="mt-3 space-y-2">
                  <div>
                    <label className="text-xs text-muted-foreground">Display Name</label>
                    <Input
                      value={t.name}
                      onChange={(e) => updateTemplateName(idx, e.target.value)}
                    />
                  </div>
                  <div>
                    <label className="text-xs text-muted-foreground">
                      Questions (one per line)
                    </label>
                    <textarea
                      value={questionsText[t.id] ?? ""}
                      onChange={(e) => updateTemplateQuestions(t.id, e.target.value)}
                      rows={Math.min(20, Math.max(6, (questionsText[t.id]?.split("\n").length ?? 6)))}
                      className="w-full rounded-md border bg-background px-3 py-2 text-sm font-mono"
                    />
                  </div>
                  <div className="flex justify-end">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => deleteTemplate(idx)}
                      className="text-destructive"
                    >
                      Delete this template
                    </Button>
                  </div>
                </div>
              </details>
            ))}
          </div>

          <p className="text-xs text-muted-foreground">
            Note: changes only persist after clicking <strong>Save All Templates</strong>.
            Server gates writes by Matrix user id (set <code>SURVEY_ADMIN_USERS</code> env on
            the server to restrict).
          </p>
        </CardContent>
      </Card>
    </div>
  );
}
